import React, { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { getSAPublicHolidays, getHolidayName } from "../utils/saPublicHolidays";

export default function ManagerCalendar() {
  const [games, setGames] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState([]);

  useEffect(() => {
    // Load all games
    const unsub = onSnapshot(collection(db, "games"), (snap) => {
      setGames(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsub;
  }, []);

  // Load public holidays when month changes
  useEffect(() => {
    const year = currentDate.getFullYear();
    setPublicHolidays(getSAPublicHolidays(year));
  }, [currentDate]);

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month, 1).getDay();
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const today = () => {
    setCurrentDate(new Date());
  };

  const openDayPopup = (day, dayGames) => {
    if (dayGames.length === 0) return;
    setSelectedDay({ day, games: dayGames, month: monthName });
    setShowPopup(true);
  };

  const closePopup = () => {
    setShowPopup(false);
    setSelectedDay(null);
  };

  // Filter games for current month
  const monthGames = games.filter((g) => {
    if (!g.date) return false;
    const gameDate = new Date(g.date);
    return (
      gameDate.getMonth() === currentDate.getMonth() &&
      gameDate.getFullYear() === currentDate.getFullYear()
    );
  });

  // Group games by day
  const gamesByDay = monthGames.reduce((acc, game) => {
    const day = new Date(game.date).getDate();
    if (!acc[day]) acc[day] = [];
    acc[day].push(game);
    return acc;
  }, {});

  // Sort games by time within each day
  Object.keys(gamesByDay).forEach(day => {
    gamesByDay[day].sort((a, b) => {
      if (!a.time || !b.time) return 0;
      return a.time.localeCompare(b.time);
    });
  });

  // Get status color
  const getStatusColor = (game) => {
    switch (game.status) {
      case "scheduled":
        return "bg-green-100 border-l-2 border-green-600";
      case "assigned":
        return "bg-blue-100 border-l-2 border-blue-600";
      case "pending":
        return "bg-yellow-100 border-l-2 border-yellow-600";
      case "completed":
        return "bg-gray-100 border-l-2 border-gray-600";
      case "cancelled":
        return "bg-red-100 border-l-2 border-red-600";
      default:
        return "bg-gray-100 border-l-2 border-gray-600";
    }
  };

  // Build calendar grid
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const monthName = currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const calendarDays = [];
  
  // Empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(<div key={`empty-${i}`} className="border p-2 bg-gray-50 min-h-[120px]"></div>);
  }

  // Days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const isToday =
      day === new Date().getDate() &&
      currentDate.getMonth() === new Date().getMonth() &&
      currentDate.getFullYear() === new Date().getFullYear();

    const dayGames = gamesByDay[day] || [];
    
    // Check if this day is a public holiday
    const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const holidayName = getHolidayName(dateString, currentDate.getFullYear());

    calendarDays.push(
      <div
        key={day}
        className={`border p-2 min-h-[120px] ${
          holidayName ? "bg-red-50 border-red-300" : isToday ? "bg-blue-50 border-blue-400" : "bg-white"
        } ${
          dayGames.length > 0 ? "cursor-pointer hover:bg-gray-50" : ""
        }`}
        onClick={() => openDayPopup(day, dayGames)}
      >
        <div className={`text-sm font-semibold mb-1 flex justify-between items-center ${
          holidayName ? "text-red-700" : isToday ? "text-blue-700" : "text-gray-700"
        }`}>
          <span>
            {day}
            {isToday && <span className="ml-1 text-xs">(Today)</span>}
            {holidayName && <span className="ml-1 text-xs">🇿🇦</span>}
          </span>
          {dayGames.length > 0 && (
            <span className="text-xs bg-gray-700 text-white px-1 rounded">
              {dayGames.length}
            </span>
          )}
        </div>
        
        {/* Public Holiday Banner */}
        {holidayName && (
          <div className="bg-red-600 text-white text-xs px-2 py-1 rounded mb-1 font-semibold text-center">
            {holidayName}
          </div>
        )}
        
        <div className="space-y-1">
          {dayGames.slice(0, 3).map((game) => (
            <Link
              key={game.id}
              to={`/game/${game.id}`}
              className={`block text-xs p-1 rounded hover:opacity-80 ${getStatusColor(game)}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="font-semibold truncate">
                {game.time} - {game.ageGroup}
              </div>
              <div className="truncate text-gray-700">
                {game.teamA} vs {game.teamB}
              </div>
              <div className="text-gray-600 truncate">{game.venue}</div>
              {game.assignedRefereeName && (
                <div className="text-green-700 text-xs truncate">
                  ✓ {game.assignedRefereeName}
                </div>
              )}
            </Link>
          ))}
          {dayGames.length > 3 && (
            <div className="text-xs text-center text-gray-600 font-semibold">
              +{dayGames.length - 3} more...
            </div>
          )}
        </div>
      </div>
    );
  }

  // Statistics
  const statusCounts = monthGames.reduce((acc, game) => {
    const status = game.status || "pending";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Games Calendar</h2>
        <div className="flex items-center space-x-3">
          <button
            onClick={previousMonth}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            ← Previous
          </button>
          <button
            onClick={today}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Today
          </button>
          <button
            onClick={nextMonth}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Next →
          </button>
        </div>
      </div>

      {/* MONTH TITLE & STATS */}
      <div className="text-center mb-4">
        <h3 className="text-xl font-semibold">{monthName}</h3>
        <div className="flex justify-center gap-4 mt-2 text-sm">
          <span className="text-gray-600">
            Total: <strong>{monthGames.length}</strong>
          </span>
          {statusCounts.pending > 0 && (
            <span className="text-yellow-700">
              Pending: <strong>{statusCounts.pending}</strong>
            </span>
          )}
          {statusCounts.assigned > 0 && (
            <span className="text-blue-700">
              Assigned: <strong>{statusCounts.assigned}</strong>
            </span>
          )}
          {statusCounts.scheduled > 0 && (
            <span className="text-green-700">
              Scheduled: <strong>{statusCounts.scheduled}</strong>
            </span>
          )}
        </div>
      </div>

      {/* CALENDAR GRID */}
      <div className="bg-white rounded shadow overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-gray-800 text-white">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="p-2 text-center font-semibold">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">{calendarDays}</div>
      </div>

      {/* LEGEND */}
      <div className="mt-6 bg-white p-4 rounded shadow">
        <h4 className="font-semibold mb-2">Legend:</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-yellow-100 border-l-2 border-yellow-600 mr-2"></div>
            <span>Pending (No Referee Assigned)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-blue-100 border-l-2 border-blue-600 mr-2"></div>
            <span>Assigned (Awaiting Acceptance)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-100 border-l-2 border-green-600 mr-2"></div>
            <span>Scheduled (Referee Confirmed)</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-gray-100 border-l-2 border-gray-600 mr-2"></div>
            <span>Completed</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-100 border-l-2 border-red-600 mr-2"></div>
            <span>Cancelled</span>
          </div>
        </div>
      </div>

      {/* DAY POPUP MODAL */}
      {showPopup && selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closePopup}>
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
              <h3 className="text-xl font-bold">
                {selectedDay.month} {selectedDay.day} - {selectedDay.games.length} Game{selectedDay.games.length !== 1 ? 's' : ''}
              </h3>
              <button onClick={closePopup} className="text-white hover:text-gray-300 text-2xl font-bold">
                ×
              </button>
            </div>

            {/* Games List */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {selectedDay.games.map((game) => (
                  <div key={game.id} className={`p-4 rounded-lg border-l-4 ${getStatusColor(game)}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-bold text-lg">
                          {game.teamA} vs {game.teamB}
                        </div>
                        <div className="text-sm text-gray-600">
                          {game.time} • {game.ageGroup} • {game.competition}
                        </div>
                      </div>
                      <div className="text-sm font-semibold px-3 py-1 bg-gray-200 rounded">
                        {game.status?.toUpperCase() || 'PENDING'}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                      <div>
                        <span className="font-semibold">Venue:</span> {game.venue}
                      </div>
                      <div>
                        <span className="font-semibold">Field:</span> {game.field}
                      </div>
                      {game.assignedRefereeName && (
                        <>
                          <div className="col-span-2">
                            <span className="font-semibold">Referee:</span> {game.assignedRefereeName}
                          </div>
                        </>
                      )}
                      {game.coachName && (
                        <div className="col-span-2">
                          <span className="font-semibold">Requested by:</span> {game.coachName}
                        </div>
                      )}
                    </div>

                    <div className="mt-3">
                      <Link
                        to={`/game/${game.id}`}
                        className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                        onClick={closePopup}
                      >
                        View Details →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
